import { useForm } from "@tanstack/react-form";
import { z } from "zod";
import { Loader2 } from "lucide-react";

import {
  Form, FormControl, FormField, FormItem, FormLabel, FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

import { useBoats } from "@/features/inventory/hooks";
import { TechnicianSelect, TECHNICIAN_UNASSIGNED } from "@/features/service/technician-select";
import type { ServiceScheduleWriteBody } from "@/types/service";

const scheduleFormSchema = z.object({
  boat_id: z.string().min(1, "Boat is required"),
  title: z.string().min(1, "Title is required"),
  description: z.string().optional(),
  interval_days: z.coerce.number().int().gt(0, "Interval must be at least 1 day"),
  interval_hours: z.union([z.coerce.number().int().gt(0, "Hours must be positive"), z.literal("")]).optional(),
  reminder_lead_days: z.coerce.number().int().gte(0, "Cannot be negative"),
  auto_create_work_order: z.boolean(),
  priority: z.enum(["low", "medium", "high", "urgent"]),
  assigned_technician: z.string().optional(),
});

type ScheduleFormValues = z.infer<typeof scheduleFormSchema>;
const fieldSchemas = scheduleFormSchema.shape;

interface ScheduleFormProps {
  /** Lock the form to one boat (hides the picker). */
  boatId?: string;
  onSubmit: (body: ServiceScheduleWriteBody) => void;
  isPending?: boolean;
}

export function ScheduleForm({ boatId, onSubmit, isPending }: ScheduleFormProps) {
  const { data: boats = [] } = useBoats();

  const form = useForm({
    defaultValues: {
      boat_id: boatId ?? "",
      title: "",
      description: "",
      interval_days: 180,
      interval_hours: "",
      reminder_lead_days: 14,
      auto_create_work_order: true,
      priority: "medium",
      assigned_technician: "",
    } as ScheduleFormValues,
    onSubmit: ({ value }) => {
      onSubmit({
        boat_id: value.boat_id,
        title: value.title,
        description: value.description || undefined,
        interval_days: Number(value.interval_days),
        interval_hours:
          value.interval_hours === "" || value.interval_hours === undefined
            ? undefined
            : Number(value.interval_hours),
        reminder_lead_days: Number(value.reminder_lead_days),
        auto_create_work_order: value.auto_create_work_order,
        priority: value.priority,
        assigned_technician:
          !value.assigned_technician || value.assigned_technician === TECHNICIAN_UNASSIGNED
            ? undefined
            : value.assigned_technician,
      });
    },
  });

  return (
    <Form form={form}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit();
        }}
        className="space-y-4"
      >
        {!boatId && (
          <FormField
            name="boat_id"
            validators={{ onChange: fieldSchemas.boat_id }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Boat</FormLabel>
                <Select onValueChange={field.onChange} value={field.value as string}>
                  <FormControl><SelectTrigger><SelectValue placeholder="Select a boat" /></SelectTrigger></FormControl>
                  <SelectContent>
                    {boats.map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.make} {b.model} ({b.year})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        <FormField
          name="title"
          validators={{ onChange: fieldSchemas.title }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Task</FormLabel>
              <FormControl><Input placeholder="Engine oil & filter change" {...field} /></FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl><Textarea rows={2} {...field} /></FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="grid grid-cols-2 gap-4">
          <FormField
            name="interval_days"
            validators={{ onChange: fieldSchemas.interval_days }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Every (days)</FormLabel>
                <FormControl><Input type="number" min={1} {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            name="interval_hours"
            validators={{ onChange: fieldSchemas.interval_hours }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>…or every (engine hours)</FormLabel>
                <FormControl><Input type="number" min={1} placeholder="optional" {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <FormField
            name="priority"
            validators={{ onChange: fieldSchemas.priority }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Priority</FormLabel>
                <Select onValueChange={field.onChange} value={field.value as string}>
                  <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            name="reminder_lead_days"
            validators={{ onChange: fieldSchemas.reminder_lead_days }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Remind owner (days ahead)</FormLabel>
                <FormControl><Input type="number" min={0} {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <FormField
          name="assigned_technician"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Technician</FormLabel>
              <FormControl>
                <TechnicianSelect
                  value={(field.value as string) || ""}
                  onChange={field.onChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          name="auto_create_work_order"
          render={({ field }) => (
            <FormItem>
              <label className="flex cursor-pointer items-center gap-2 text-sm">
                <Checkbox
                  checked={field.value as unknown as boolean}
                  onCheckedChange={(checked) => field.onChange(checked === true)}
                />
                Automatically open a work order when due
              </label>
            </FormItem>
          )}
        />
        <Button type="submit" disabled={isPending} className="w-full">
          {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Create schedule
        </Button>
      </form>
    </Form>
  );
}
